import React from "react";
import { Stack, Box } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch } from "../redux/store";
import {
  createQuantityUnit,
  updateQuantityUnit,
} from "../redux/actions/quantityUnitActions";
import { cleanFormData } from "../helpers/formHelpers";
import { quantityUnitActions } from "../redux/slices/quantityUnit";

import { QuantityUnitRequestDto } from "../shared/dtos/quantityUnit/QuantityUnitRequestDto";
import { QuantityUnitResponseDto } from "../shared/dtos/quantityUnit/QuantityUnitResponseDto";

const quantityUnitSchema = Yup.object().shape({
  name: Yup.string().required("Unit name is required (e.g., Litre)"),
  code: Yup.string().required("Unit code is required (e.g., L)"),
});

interface CreateOrUpdateQuantityUnitFormProps {
  unit?: QuantityUnitResponseDto | null;
  isEditing?: boolean;
  loading: boolean;
  error: string | null;
}

const CreateOrUpdateQuantityUnitForm: React.FC<
  CreateOrUpdateQuantityUnitFormProps
> = ({ unit = null, isEditing = false, loading = false, error = null }) => {
  const dispatch = useAppDispatch();

  const initialValues: Partial<QuantityUnitRequestDto> = {
    name: unit?.name || "",
    code: unit?.code || "",
  };

  return (
    <Box sx={{ width: "100%" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={() =>
          dispatch(
            isEditing
              ? quantityUnitActions.setErrorUpdate(null)
              : quantityUnitActions.setErrorCreate(null),
          )
        }
      />
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={quantityUnitSchema}
        onSubmit={(values) => {
          const payload = cleanFormData(
            values,
          ) as unknown as QuantityUnitRequestDto;
          if (isEditing && unit) {
            dispatch(updateQuantityUnit({ id: unit.id, values: payload }));
          } else {
            dispatch(createQuantityUnit(payload));
          }
        }}
      >
        {({ dirty, isValid }: FormikProps<Partial<QuantityUnitRequestDto>>) => (
          <Form>
            <Stack spacing={3}>
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr" },
                }}
              >
                <FormTextField
                  name="name"
                  label="Unit Name"
                  placeholder="e.g., Kilograms"
                />
                <FormTextField
                  name="code"
                  label="Unit Code"
                  placeholder="e.g., KG"
                />
              </Box>

              <SubmitButton
                title={isEditing ? "Save Changes" : "Create Unit"}
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={
                  isEditing ? <SaveOutlinedIcon /> : <AddCircleOutlineIcon />
                }
                fullWidth
                sx={{ py: 1.5, fontWeight: 700 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default CreateOrUpdateQuantityUnitForm;
